import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { Board } from '@/api';
import { errorMessage } from '@/api/client';
import BoardEditorModal from '@/components/BoardEditorModal.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import { useBoardsStore } from '@/stores/boards';
import { useMenuStore } from '@/stores/menu';
import { useModalStore } from '@/stores/modals';
import { useUiStore } from '@/stores/ui';
import { copia } from '@/utils/appunti';

/* Azioni su una board, e il menu contestuale che le raccoglie.
 *
 * Il gemello di usePinActions, nato per lo stesso motivo: le stesse quattro
 * cose si facevano dalla griglia delle board e da nessun altro posto, mentre
 * la colonna a sinistra — dove le board si guardano davvero, tutto il giorno —
 * non ne offriva nessuna. Ora la logica sta qui una volta sola e i due punti
 * di ingresso (colonna e griglia) chiamano lo stesso elenco di voci.
 *
 * Ogni azione, riuscita, aggiorna da sé le due liste che l'app tiene sempre a
 * schermo: ricarica la colonna e segnala la mutazione allo store, che è quel
 * che serve alla griglia per togliere o sostituire la tessera. Lo fa qui e non
 * nei chiamanti perché è sempre giusto, mentre affidarlo a ciascuno significava
 * che l'eliminazione fatta dalla colonna lasciava la board nella griglia
 * (svuotata dei suoi pin, quindi apparentemente "diventata vuota") fino al
 * ricaricamento della pagina.
 *
 * L'esito torna comunque al chiamante, per quello che solo lui può sapere: la
 * colonna, se la board eliminata era quella aperta, se ne va altrove.
 */
export function useBoardActions() {
  const modals = useModalStore();
  const menu = useMenuStore();
  const boards = useBoardsStore();
  const ui = useUiStore();
  const router = useRouter();
  const { t } = useI18n();

  /** @returns la board aggiornata, o undefined se annullato. */
  async function rinomina(board) {
    const salvata = await modals.open(BoardEditorModal, { board });
    if (salvata) {
      boards.reload();
      boards.segnalaMutazione('modificata', salvata);
      ui.notify(t('boardSaved'));
    }
    return salvata;
  }

  /** Inverte privata/pubblica senza passare dal modulo. @returns la board. */
  async function cambiaPrivacy(board) {
    const nuova = !board.private;
    try {
      const salvata = await Board.update(board.id, { private: nuova });
      boards.reload();
      boards.segnalaMutazione('modificata', salvata);
      ui.notify(t(nuova ? 'boardNowPrivate' : 'boardNowPublic'));
      return salvata;
    } catch (errore) {
      ui.notifyError(errorMessage(errore, t('boardSaveFailed')));
      return undefined;
    }
  }

  /** @returns true se la board è stata davvero eliminata. */
  async function elimina(board) {
    const conferma = await modals.open(ConfirmDialog, {
      message: t('confirmDeleteBoard'),
      confirmLabel: t('deleteButton'),
      destructive: true,
    });
    if (!conferma) return false;
    try {
      await Board.remove(board.id);
      boards.reload();
      boards.segnalaMutazione('eliminata', board);
      ui.notify(t('boardDeleted'));
      return true;
    } catch (errore) {
      ui.notifyError(errorMessage(errore, t('boardDeleteFailed')));
      return false;
    }
  }

  function indirizzo(board) {
    const { href } = router.resolve({ name: 'pins-by-board', params: { boardId: board.id } });
    return new URL(href, window.location.origin).toString();
  }

  async function copiaLink(board) {
    ui.notify(t((await copia(indirizzo(board))) ? 'linkCopied' : 'copyFailed'));
  }

  /* Il menu vero e proprio.
   *
   * `mia` decide se si vedono le voci che modificano: dalla colonna sono
   * sempre le proprie, dalla griglia possono essere di chiunque. Le voci di
   * sola lettura (apri, copia il link) restano comunque, perché su una board
   * altrui sono le uniche sensate e un menu che si apre vuoto sembra rotto —
   * per questo su una board altrui il menu si apre lo stesso.
   *
   * `dopoModifica` e `dopoElimina` sono per ciò che riguarda solo chi chiama
   * (la colonna: andarsene se la board aperta è quella eliminata). Le liste si
   * aggiornano da sole, vedi sopra: non serve agganciarle qui.
   */
  function apriMenu(evento, board, { mia = true, dopoModifica, dopoElimina } = {}) {
    menu.apri(
      evento,
      [
        {
          etichetta: t('openBoardItem'),
          azione: () => router.push({ name: 'pins-by-board', params: { boardId: board.id } }),
        },
        { separatore: true },
        mia && {
          etichetta: t('renameBoardItem'),
          azione: async () => {
            const salvata = await rinomina(board);
            if (salvata) dopoModifica?.(salvata);
          },
        },
        /* La spunta e non due voci alternative: lo stato della board è ciò che
         * si vuole sapere aprendo il menu, e "Privata ✓" lo dice mentre lo
         * offre.
         *
         * Sparisce se lo stato non lo conosciamo: una spunta spenta per
         * ignoranza direbbe "pubblica", e il clic successivo la renderebbe
         * privata credendo di leggere quello che ha appena mostrato. */
        mia &&
          typeof board.private === 'boolean' && {
            etichetta: t('privateItem'),
            spunta: board.private,
            azione: async () => {
              const salvata = await cambiaPrivacy(board);
              if (salvata) dopoModifica?.(salvata);
            },
          },
        { separatore: true },
        { etichetta: t('copyLinkItem'), azione: () => copiaLink(board) },
        { separatore: true },
        mia && {
          etichetta: t('deleteBoardItem'),
          pericolo: true,
          azione: async () => {
            if (await elimina(board)) dopoElimina?.(board);
          },
        },
      ],
      { titolo: board.name },
    );
  }

  return { rinomina, cambiaPrivacy, elimina, copiaLink, apriMenu };
}
